package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initMetrikPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("metrik.md", FrontMatterElement.Builder { addScalar("title", "metrik"); addScalar("searchTitle", "Monitoring for Ktor services without Prometheus — Kotlin/Native and SQLite"); addScalar("description", "Four questions — how many requests, how many errors, how slow, is memory running out — answered by one native binary. The agent costs 106 ns per request."); addScalar("library", "metrik"); addScalar("post", "/blog/percentiles-dont-average"); }))
}

@Page("/metrik")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun MetrikPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A Prometheus, a Grafana and a time-series database is a lot of moving parts to answer four questions. metrik answers them from one binary and one SQLite file, with no scrape configuration and no service discovery — and writes to Telegram when the answers get worse.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("An agent that costs the monitored service ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("106 nanoseconds per request")
            }
            org.jetbrains.compose.web.dom.Text(" and one UDP packet a minute. Aggregation happens in-process; nothing is scraped.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Percentiles merged, not averaged.")
            }
            org.jetbrains.compose.web.dom.Text(" Three instances reporting p99 of 100 ms, 100 ms and 400 ms do not have a service p99 of 200 ms — that number means nothing. Exponential histograms are merged and the quantile read from the result.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Route templates as labels")
            }
            org.jetbrains.compose.web.dom.Text(" (")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("/users/{id}")
            }
            org.jetbrains.compose.web.dom.Text("), so cardinality stays bounded instead of growing one series per user.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Deploy markers on every chart")
            }
            org.jetbrains.compose.web.dom.Text(", because \"worse\" is only actionable with \"worse after what\".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Alerts on error rate, latency, memory and ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("silence")
            }
            org.jetbrains.compose.web.dom.Text(", with hysteresis and a cooldown.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Three ways to read it: a Compose Multiplatform dashboard, a terminal client, and an MCP endpoint for an agent.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""docker run -p 8080:8080 ghcr.io/youndie/metrik:latest
""") } }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories { maven("https://reposilite.kotlin.website/snapshots") }
dependencies { implementation("io.github.youndie.metrik:agent:<VERSION>") }
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Live at ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://metrik.kotlin.website") {
            org.jetbrains.compose.web.dom.Text("metrik.kotlin.website")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
}
